import React from 'react';
import { ButtonProps } from '@mui/material';
import { Button } from '@vibTheme/components/_base/Button';

type Option<T> = { value: T; label: string };

type Variant = 'card' | 'pill';

type State = { variant: ButtonProps['variant']; className: string };

const VARIANTS: Record<
  Variant,
  { group: string; selected: State; unselected: State }
> = {
  card: {
    group: 'flex flex-wrap gap-sm',
    selected: {
      variant: 'contained',
      // Borda nos dois estados: o `outlined` ao lado tem 1px e o box-sizing do globals a conta na altura.
      className: '!rounded-default border border-primary',
    },
    unselected: {
      variant: 'outlined',
      // A importância vence o `rounded-full` do Button do tema, que o twMerge não desduplica por ser chave customizada.
      className:
        '!rounded-default bg-white text-[#374151] border-[#E5E7EB] shadow-default',
    },
  },
  pill: {
    group:
      'inline-flex shrink-0 items-center rounded-full border border-[#E5E7EB] bg-[#F3F4F6] p-[2px]',
    selected: { variant: 'contained', className: '' },
    unselected: { variant: 'text', className: 'text-[#374151]' },
  },
};

// `shrink-0` + `whitespace-nowrap`: encolhendo, o rótulo quebrava e o fundo do vizinho passava por cima.
// Padding e corpo de letra à mão porque nenhum `size` do MUI dá os 14px do desenho.
const OPTION_BASE = 'px-md py-[6px] text-sm shrink-0 whitespace-nowrap';

/**
 * Grupo de botões onde exatamente um está em uso.
 *
 * Usa o Button do tema, não `<button>`: com o preflight do Tailwind desligado, o nativo herda o cinza e a
 * Arial que o navegador impõe a controle de formulário.
 */
export function SegmentedToggle<T extends string | number>({
  options,
  value,
  onChange,
  label,
  variant = 'card',
}: {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  variant?: Variant;
}) {
  const styles = VARIANTS[variant];

  return (
    <div className={styles.group} role="group" aria-label={label}>
      {options.map((option) => {
        const isSelected = option.value === value;
        const state = isSelected ? styles.selected : styles.unselected;

        return (
          <Button
            key={String(option.value)}
            size="small"
            variant={state.variant}
            disableElevation
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={`${OPTION_BASE} ${state.className}`}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
